"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useSiteConfig } from "@/lib/SiteConfigContext";
import { PROJECT_CATEGORIES, type GalleryItem, type ProjectCategory } from "@/lib/siteConfig";

type Filter = "All Projects" | ProjectCategory;

export default function ProjectsGallery() {
  const { gallery } = useSiteConfig();
  const [filter, setFilter] = useState<Filter>("All Projects");
  const [index, setIndex] = useState<number | null>(null);

  const active = useMemo(
    () => gallery.filter((g) => g.active).sort((a, b) => a.order - b.order),
    [gallery],
  );
  const shown = useMemo(
    () => (filter === "All Projects" ? active : active.filter((g) => g.category === filter)),
    [active, filter],
  );

  // Reset any open lightbox when the filter changes (indices shift).
  useEffect(() => setIndex(null), [filter]);

  const filters: Filter[] = ["All Projects", ...PROJECT_CATEGORIES];

  return (
    <div>
      <div className="flex flex-wrap justify-center gap-2">
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              filter === f
                ? "bg-gold text-white"
                : "bg-cream text-navy hover:bg-cream-dark"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="mt-12 text-center text-muted">No projects in this category yet.</p>
      ) : (
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3">
          {shown.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(i)}
              className="gallery-card group relative aspect-square"
              aria-label={`View ${item.alt || item.category}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={item.url}
                alt={item.alt}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <span className="pointer-events-none absolute inset-0 flex items-end bg-gradient-to-t from-navy-900/75 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                <span className="text-xs font-semibold uppercase tracking-wide text-white">
                  {item.category}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {index !== null && shown[index] && (
        <Lightbox
          item={shown[index]}
          onClose={() => setIndex(null)}
          onPrev={() => setIndex((v) => (v === null ? null : (v + shown.length - 1) % shown.length))}
          onNext={() => setIndex((v) => (v === null ? null : (v + 1) % shown.length))}
        />
      )}
    </div>
  );
}

function Lightbox({
  item,
  onClose,
  onPrev,
  onNext,
}: {
  item: GalleryItem;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/90 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={item.alt || item.category}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 text-white/80 hover:text-white"
      >
        <X className="h-8 w-8" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        aria-label="Previous"
        className="absolute left-3 text-white/80 hover:text-white sm:left-6"
      >
        <ChevronLeft className="h-10 w-10" />
      </button>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={item.url}
        alt={item.alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[90vw] rounded object-contain shadow-2xl"
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        aria-label="Next"
        className="absolute right-3 text-white/80 hover:text-white sm:right-6"
      >
        <ChevronRight className="h-10 w-10" />
      </button>
    </div>
  );
}
